import React from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import Loader from '@/shared/components/Loader';
import { formatAudioTime } from '@/shared/lib/formatDuration'
import { useAudioControls } from '@/shared/audio/useAudioControls'

export default function InlineAudioPlayer({
  src,
  variant = "draft",
  playbackRate = 1,
  onMediaError,
  disabled = false,
  className = "",
  urlLoading = false,
  knownDurationSeconds = 0,
}) {
  const {
    audioRef,
    duration,
    currentTime,
    playing,
    mediaReady,
    hasSrc,
    togglePlay,
    onSeek,
    maxDuration,
    seekValue,
    seekDisabled,
    progressPct,
    suppressMediaErrorRef,
  } = useAudioControls({
    src,
    playbackRate,
    disabled,
    urlLoading,
    knownDurationSeconds,
  });

  const isReport = variant === "report";
  const awaitingUrl = isReport && urlLoading && !hasSrc;
  const awaitingMedia = isReport && hasSrc && !mediaReady;
  const showPlaySpinner = isReport && (awaitingUrl || awaitingMedia);

  if (!isReport && !src) return null;
  if (isReport && !src && !urlLoading) return null;

  const seekClass =
    isReport
      ? "inline-audio-player__seek inline-audio-player__seek--report flex-1 min-w-0 self-center"
      : "inline-audio-player__seek inline-audio-player__seek--draft flex-1 min-w-0 self-center";

  const timeClass = isReport
    ? "shrink-0 tabular-nums text-base font-normal text-[#71717a]"
    : "shrink-0 tabular-nums text-[11px] text-amber-900/90 sm:ml-3";

  const endTimeClass = isReport
    ? "shrink-0 tabular-nums text-base font-normal text-[#71717a] text-right"
    : "shrink-0 tabular-nums text-[11px] text-amber-900/90 text-right sm:mr-1";

  const rowClass = isReport
    ? `inline-audio-player inline-audio-player--report flex w-full max-w-full items-center gap-2.5 sm:gap-3 min-h-10 ${className}`
    : `inline-audio-player flex w-full max-w-full items-center gap-1.5 sm:gap-2 min-h-8 ${className}`;

  const buttonClass = isReport
    ? "mr-1 shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-black text-white hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-400 focus:ring-offset-1 disabled:opacity-40 disabled:pointer-events-none"
    : "mr-3 sm:mr-1 shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-amber-600 text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 disabled:opacity-40 disabled:pointer-events-none";

  const playIconClass = isReport ? "h-4 w-4 ml-0.5" : "h-3 w-3 ml-px";
  const pauseIconClass = isReport ? "h-4 w-4" : "h-3 w-3";

  const seekInputStyle =
    isReport && !seekDisabled
      ? { "--progress": `${progressPct}%` }
      : isReport
        ? { "--progress": "0%" }
        : undefined;

  return (
    <div className={rowClass}>
      <span className={timeClass}>{formatAudioTime(currentTime)}</span>

      <input
        type="range"
        className={seekClass}
        style={seekInputStyle}
        min={0}
        max={maxDuration}
        step={0.01}
        disabled={seekDisabled}
        value={seekValue}
        onChange={onSeek}
        aria-label="Seek"
      />

      <span className={endTimeClass}>{formatAudioTime(duration)}</span>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          togglePlay();
        }}
        onPointerDown={(event) => event.stopPropagation()}
        disabled={disabled || (!showPlaySpinner && (!hasSrc || !mediaReady))}
        className={`${buttonClass} ${showPlaySpinner ? "opacity-100 pointer-events-none cursor-wait" : ""}`}
        aria-label={playing ? "Pause" : "Play"}
        aria-busy={showPlaySpinner}
      >
        {showPlaySpinner ? (
          <Loader
            variant="small"
            className="flex items-center justify-center pointer-events-none"
            spinnerClassName={isReport ? "!h-6 !w-6" : "!h-4 !w-4"}
          />
        ) : playing ? (
          <FaPause className={pauseIconClass} />
        ) : (
          <FaPlay className={playIconClass} />
        )}
      </button>

      {hasSrc ? (
        <audio
          ref={audioRef}
          playsInline
          preload="metadata"
          src={src}
          className="hidden"
          onError={(event) => {
            if (suppressMediaErrorRef.current) return;
            onMediaError?.(event);
          }}
        />
      ) : null}
    </div>
  );
}
